/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Stack, styled, Typography } from "@mui/material";
import React, { type JSX, PropsWithChildren, useEffect, useRef, useState } from "react";

import PlaceholderImage from "../../../../assets/images/image-placeholder.png";
import { Button } from "../../../../components/Button";
import {
  IUnitPriceFormRef,
  IUnitPriceState,
  TUnitPriceFormData,
} from "../../../../components/forms/UnitPriceForm/types";
import { UnitPriceForm } from "../../../../components/forms/UnitPriceForm/UnitPriceForm";
import ImageWithPlaceholder from "../../../../components/ImageWithPlaceholder";
import Modal from "../../../../components/Modal";
import TestIds from "../../../../testIds";
import { isPriceDifferent } from "../../../../util/formatting";
import { formatPrice, generateImageUrl } from "../../../../util/helpers";
import PrintVariants from "../../../products/components/ProductCard/PrintVariants";
import { ProductPriceDetails } from "../../../products/components/ProductCard/ProductPriceDetails";
import { PriceStyle } from "../../../products/components/ProductCard/types";

import { SalesAgreementPromoProductSelectionVariant } from "./types";

export interface IEditedSAItemDetails {
  editedPrice: number;
  editedPriceReasonCode: string | undefined;
  editedPriceReasonNote: string | undefined;
  item: SalesAgreementPromoProductSelectionVariant;
}

export interface IEditSalesAgreementProductPriceModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  item?: SalesAgreementPromoProductSelectionVariant;
  onApplyEditedPrice: (editedSAItemDetails: IEditedSAItemDetails) => void;
  onRevertEditedPrice: (item: SalesAgreementPromoProductSelectionVariant) => void;
}

const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

export const EditSalesAgreementProductPriceModal = ({
  open,
  setOpen,
  item,
  onApplyEditedPrice,
  onRevertEditedPrice,
}: PropsWithChildren<IEditSalesAgreementProductPriceModalProps>): JSX.Element | null => {
  const unitPriceFormRef = useRef<IUnitPriceFormRef>(null);
  const [editedPrice, setEditedPrice] = useState<number | undefined>();

  useEffect(() => {
    if (item?.editedPrice) {
      setEditedPrice(item.editedPrice);
    } else {
      setEditedPrice(undefined);
    }
  }, [item, open]);

  const handleCloseModal = (): void => setOpen(false);

  const handleUnitPriceFormChange = (updatedUnitPriceState?: IUnitPriceState): void => {
    setEditedPrice(updatedUnitPriceState?.unitPrice);
  };

  const handleUnitPriceFormRevertChanges = (): void => {
    if (item) {
      setEditedPrice(undefined);
      onRevertEditedPrice(item);
    }

    handleCloseModal();
  };

  const applyEditedPrice = async (): Promise<void> => {
    await unitPriceFormRef.current?.submit();
  };

  const handleUnitPriceFormSubmit = (formData: TUnitPriceFormData): void => {
    const newPrice = parseFloat(formData.unitPrice || "0");
    const unitPriceChanged: boolean | undefined = isPriceDifferent(newPrice, item?.unitPrice || 0);
    const reasonChanged: boolean =
      !!(item?.lastEditedReasonCode && formData.reasonCode !== item?.lastEditedReasonCode) ||
      !!(item?.lastEditedReasonNote && formData.reasonNote !== item?.lastEditedReasonNote);
    const shouldUpdateReason: boolean = unitPriceChanged || reasonChanged;

    if (item && shouldUpdateReason) {
      onApplyEditedPrice({
        editedPrice: newPrice,
        editedPriceReasonCode: formData.reasonCode || undefined,
        editedPriceReasonNote: formData.reasonNote || undefined,
        item,
      });
    }

    handleCloseModal();
  };

  const variants = [
    ...(item?.sizeDescription ? [{ label: "Size", value: item.sizeDescription }] : []),
    ...(item?.colourDescription ? [{ label: "Colour", value: item.colourDescription }] : []),
  ];

  return item && open ? (
    <Modal
      title="Edit unit price"
      open={open}
      handleClose={handleCloseModal}
      footer={
        <StyledButton
          data-testid={TestIds.feat.editPrice.saveChanges}
          variant="contained"
          onClick={applyEditedPrice}
        >
          Save changes
        </StyledButton>
      }
      testId={TestIds.feat.editPrice.modal}
    >
      <Stack spacing={4} pb={2}>
        <Stack direction={"row"} gap={2}>
          <Box height={"80px"} width={"80px"}>
            <ImageWithPlaceholder
              src={generateImageUrl(item.itemNumber)}
              alt=""
              placeholderImageUrl={PlaceholderImage}
              size={160}
            />
          </Box>

          <Stack gap={1}>
            <Typography variant="h3">{item.description}</Typography>

            <ProductPriceDetails
              items={[
                {
                  type: PriceStyle.Discounted,
                  label: "Your price:",
                  gstLabel: "GST excl.",
                  value: formatPrice(editedPrice || item.unitPrice),
                  testId: TestIds.feat.product.card.discountedPrice,
                },
                {
                  type: PriceStyle.RRP,
                  label: "RRP:",
                  gstLabel: "GST excl.",
                  value: formatPrice(item.unitPrice),
                  testId: TestIds.feat.product.card.nationalPrice,
                },
              ]}
              size="Medium"
              sx={{
                gap: 2,
              }}
            />

            <PrintVariants items={variants} direction="row" />
          </Stack>
        </Stack>

        <UnitPriceForm
          ref={unitPriceFormRef}
          productId={item.productId}
          originalPrice={item.originalPrice || item.unitPrice}
          unitPriceState={{
            unitPrice: item.editedPrice || item.unitPrice,
            reasonCode: item?.lastEditedReasonCode,
            reasonNote: item?.lastEditedReasonNote,
          }}
          validateAllFieldsOnSubmit={!!(item?.lastEditedReasonCode || item?.lastEditedReasonNote)}
          onFormChange={handleUnitPriceFormChange}
          onSubmit={handleUnitPriceFormSubmit}
          onRevertChanges={handleUnitPriceFormRevertChanges}
        />
      </Stack>
    </Modal>
  ) : null;
};
